import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../../../config/config';
import handleOpenSweetAlert from '../../../components/handleOpenSweetAlert';
import { useSession } from '../../../components/hooks/useSession';

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) return idEmpresa.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
      } catch {}
    }
    if (s.includes(',')) return s.split(',').map(x => Number(x.trim())).filter(n => Number.isFinite(n) && n > 0);
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};


// Removed unused getCurrentDate

export const useTesoreroController = () => {
  const apiUrl = config.apiBaseUrl;
  const { idPerfil, nombrePerfil, loggedInUsername, nombreEmpresa, idEmpresa } = useSession();

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  const empresaIds = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.sort((a, b) => a - b);
  }, [idEmpresa]);
  
  const empresaIdPref = useMemo(() => (empresaIds.length > 1 ? 0 : empresaIds[0] ?? 0), [empresaIds]);
  
  const esTesorero = useMemo(() => {
    return typeof nombrePerfil === 'string' &&
      nombrePerfil.toLowerCase().includes('tesorero');
  }, [nombrePerfil]);

  const [data, setData] = useState({ contenido: [], total: 0, totalRecords: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingModalMessage, setLoadingModalMessage] = useState('Procesando...');
  
  const [fileList, setFileList] = useState([]);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [initialFileId, setInitialFileId] = useState(null);
  const [modalPagoMasivoOpen, setModalPagoMasivoOpen] = useState(false);


  const [selectedRows, setSelectedRows] = useState([]);
  const [modalFondosOpen, setModalFondosOpen] = useState(false);

  const baseFilters = useMemo(() => ({
    FECHA_INICIO: '',
    FECHA_FIN: '',
    FOLIO: '',
    EMPRESA: '',
  }), []);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);

  const ejecutarBusquedaManual = useCallback((filtrosPasados = null) => {
    setFilterData(filtrosPasados || tempFilterData);
    setTableParams(prev => ({ ...prev, page: 1 }));
  }, [tempFilterData]);

  const [tableParams, setTableParams] = useState({
    page: 1,
    pageSize: 25,
    globalQuery: '',
    sortField: null,
    sortDir: null
  });

  const [modalStates, setModalStates] = useState({
    observaciones: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'info' });

  // Columns hidden by default
  const hiddenFieldsMemo = useMemo(() => [
    'REQUI',
    'DEPARTAMENTO',
    'ESTATUS',
    'VENCIMIENTO',
    'TESORERO',
    'DIASRESTANTES',
    'HAS_FACTURA',
    'ID_FACTURA',
    'HAS_CB',
    'ID_CB',
    'HAS_OC',
    'ID_OC',
    'HAS_OTROS',
    'ID_OTROS',
    'cadenaAut',
    'DIAS_VENCIMIENTO_RAW',
    'MOTIVO_CORRECCION',
    'DETALLE_CORRECCION',
    'SECCIONES_CORREGIR',
    'REQUIERE_RECLASIFICACION',
  ], []);

  const esTesoreroAsignado = useCallback((row) => {
    if (!row) return false;

    // 1. Si es Administrador (Perfil 1), permitir siempre
    if (Number(idPerfil) === 1) return true;

    // 2. Verificar por ID (TESORERO1, TESORERO2, etc.)
    const keys = Object.keys(row);
    const matchesId = keys.some(key => {
      if (key.startsWith('TESORERO') && /^\d+$/.test(key.replace('TESORERO', ''))) {
        const val = row[key];
        return val !== 'N/A' && Number(val) === Number(usuarioId);
      }
      return false;
    });
    if (matchesId) return true;

    // 3. Fallback: Verificar por Username/Nombre (Legacy)
    const current = loggedInUsername?.toLowerCase?.()?.trim();
    const tesUser = row.TESORERO?.toLowerCase?.()?.trim();
    const tesLogin = row.TESORERO_USERNAME?.toLowerCase?.()?.trim();
    const tesName = row.USER_NAME_TESORERO?.toLowerCase?.()?.trim();

    return [tesUser, tesLogin, tesName].some(v => v && current && v === current);
  }, [loggedInUsername, usuarioId, idPerfil]);

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!apiUrl || !empresaIds.length) {
      setData({ contenido: [], total: 0 }); return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const { page, pageSize, globalQuery, sortField, sortDir } = tableParams;


      const res = await axios.post(`${apiUrl}/tesoreroTabla`, {
        idEmpresa: empresaIds,
        idPerfil,
        nombreEmpresa,
        loggedInUsername,
        filterData: {
          ...filterData,
          page,
          pageSize,
          globalQuery: globalQuery || '',
          sortField,
          sortDir
        },
      }, { signal: controller.signal });

      setData(res.data || { contenido: [], total: 0 });
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching data:', err);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, empresaIds, idPerfil, nombreEmpresa, loggedInUsername, filterData, tableParams]);

  const handleExportFetchAll = useCallback(async () => {
    if (!apiUrl || !empresaIds.length) return data;
    
    const batchSize = 500;
    
    const fetchPage = (page) => axios.post(`${apiUrl}/tesoreroTabla`, {
      idEmpresa: empresaIds,
      idPerfil,
      nombreEmpresa,
      loggedInUsername,
      filterData: {
        ...filterData,
        page,
        pageSize: batchSize,
        globalQuery: tableParams.globalQuery || '',
        sortField: tableParams.sortField,
        sortDir: tableParams.sortDir
      },
    });

    try {
      const firstResp = await fetchPage(1);
      const firstContent = Array.isArray(firstResp.data?.contenido) ? firstResp.data.contenido : [];
      const totalFromServer = firstResp.data?.total || 0;

      let allData = [];

      if (firstContent.length < batchSize) {
        allData = [...firstContent];
      } else if (totalFromServer > batchSize) {
        const totalPages = Math.min(Math.ceil(totalFromServer / batchSize), 40);
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        
        const restResponses = await Promise.all(remainingPages.map(fetchPage));
        
        allData = [
          ...firstContent,
          ...restResponses.flatMap(r => Array.isArray(r.data?.contenido) ? r.data.contenido : []),
        ];
      } else {
        allData = [...firstContent];
        let currentPage = 2;
        let hasMore = true;

        while (hasMore) {
          const nextResp = await fetchPage(currentPage);
          const nextContent = Array.isArray(nextResp.data?.contenido) ? nextResp.data.contenido : [];
          if (nextContent.length > 0) {
            allData = [...allData, ...nextContent];
            hasMore = nextContent.length === batchSize;
            currentPage++;
          } else {
            hasMore = false;
          }
          if (currentPage > 40) break;
        }
      }

      return { total: allData.length, contenido: allData };
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return data;
    }
  }, [apiUrl, empresaIds, idPerfil, nombreEmpresa, loggedInUsername, filterData, data, tableParams]);

  useEffect(() => {
    fetchData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData]);

  const handleTableStateChange = useCallback((newState) => {
    const newGQ = newState.globalQuery || '';
    const newSF = newState.sortConfig?.field || null;
    const newSD = newState.sortConfig?.direction || null;
    const newPage = (newState.page ?? 0) + 1;
    const newPageSize = newState.rowsPerPage || 25;

    setTableParams(prev => {
      const resetPage = prev.globalQuery !== newGQ ||
                        prev.sortField !== newSF ||
                        prev.sortDir !== newSD ||
                        prev.pageSize !== newPageSize;

      const nextRow = {
        page: resetPage ? 1 : newPage,
        pageSize: newPageSize,
        globalQuery: newGQ,
        sortField: newSF,
        sortDir: newSD
      };

      if (JSON.stringify(prev) === JSON.stringify(nextRow)) return prev;
      return nextRow;
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    setTableParams(prev => {
      if (loading) return prev;
      return { ...prev, page: prev.page + 1 };
    });
  }, [loading]);



  const correoConfirmarPrecio = useCallback(async (row) => {
    if (Number(row.CONFIRMARPRECIO) === 1) {
      handleOpenSweetAlert('El precio ya ha sido confirmado.', 'info');
      return;
    }
    setLoadingModalMessage('Enviando correo...');
    setLoadingModal(true);
    try {
      const empParam = empresaIds.length ? empresaIds.join(',') : '';
      const res = await axios.get(`${apiUrl}/tesoreroCorreoPrecios/${row.ID}/${loggedInUsername}/${row.EMPRESA}/${empParam}`);
      handleOpenSweetAlert(res.data?.message || 'Correo enviado correctamente', 'success');
    } catch (err) {
      handleOpenSweetAlert(err.response?.data?.error || 'Error al enviar el correo', 'error');
    } finally {
      setLoadingModal(false);
      fetchData();
    }
  }, [apiUrl, empresaIds, loggedInUsername, fetchData]);

  const deriveCategory = useCallback((filepath = '') => {
    const normalized = filepath.replace(/\\/g, '/').toLowerCase();
    if (normalized.includes('/factura/') || normalized.includes('\\factura\\')) return 'FACTURA';
    if (normalized.includes('/datos bancarios/') || normalized.includes('datos_bancarios') || normalized.includes('caratula')) return 'DATOS BANCARIOS';
    if (normalized.includes('/orden') || normalized.includes('ordencompra') || normalized.includes('/oc/')) return 'OC';
    return 'OTROS';
  }, []);

  const handleViewFile = useCallback(async (row) => {
    try {
      const res = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: row.ID });
      const filesMapped = (res.data?.adjuntos || []).map(file => {
        const ext = file.filename.split('.').pop().toLowerCase();
        const mime = ext === 'pdf' ? 'application/pdf' : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : ext === 'xml' ? 'application/xml' : 'application/octet-stream';
        return {
          id: file.archivo_id, 
          filename: file.filename, 
          fileType: mime,
          previewUrl: `${apiUrl}/visualizarPagosAut/${file.archivo_id}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${file.archivo_id}`,
          category: deriveCategory(file.filepath || ''),
        };
      });
      if (!filesMapped.length) {
        handleOpenSweetAlert('No hay archivos adjuntos.', 'info'); 
        return;
      }
      setFileList(filesMapped);
      setInitialFileId(null);
      setFileModalOpen(true);

    } catch (err) {
      handleOpenSweetAlert('Error al obtener archivos', 'error');
    }
  }, [apiUrl, deriveCategory]);

  const handleViewSpecificFile = useCallback(async (row, targetFileId) => {
    if (!targetFileId) return;
    try {
      const res = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: row.ID });
      const adjuntos = res.data?.adjuntos || [];

      let targetIds = [targetFileId];
      if (typeof targetFileId === 'string' && targetFileId.startsWith('[')) {
        try { targetIds = JSON.parse(targetFileId); } catch (e) { targetIds = [targetFileId]; }
      }

      const filteredAdjuntos = adjuntos.filter(a =>
        targetIds.some(id => String(id) === String(a.archivo_id)) || deriveCategory(a.filepath || '') === 'OTROS'
      );

      if (!filteredAdjuntos.length) {
        handleOpenSweetAlert('No se encontraron archivos para mostrar.', 'warning');
        return;
      }

      setInitialFileId(targetIds);

      const filesMapped = filteredAdjuntos.map(file => {
        const ext = file.filename.split('.').pop().toLowerCase();
        const mime = ext === 'pdf' ? 'application/pdf' : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : ext === 'xml' ? 'application/xml' : 'application/octet-stream';
        return {
          id: file.archivo_id, 
          filename: file.filename, 
          fileType: mime,
          previewUrl: `${apiUrl}/visualizarPagosAut/${file.archivo_id}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${file.archivo_id}`,
          category: deriveCategory(file.filepath || ''),
        };
      });

      setFileList(filesMapped);
      setFileModalOpen(true);
    } catch (err) {
      handleOpenSweetAlert('Error al obtener archivo específico.', 'error');
    }
  }, [apiUrl, deriveCategory]);

  const openSolicitarFondosModal = useCallback(() => {
    if (!selectedRows.length) return;

    // Split into valid (confirmed + not vencida + assigned) and excluded
    const validas = selectedRows.filter(r => {
      const confirmado = Number(r.CONFIRMARPRECIO) === 1 || r.CONFIRMARPRECIO === true;
      const noVencida = Number(r.DIASRESTANTES ?? 0) >= 0;
      const asignado = esTesoreroAsignado(r);
      return confirmado && noVencida && asignado;
    });
    const excluidas = selectedRows.filter(r => {
      const confirmado = Number(r.CONFIRMARPRECIO) === 1 || r.CONFIRMARPRECIO === true;
      const noVencida = Number(r.DIASRESTANTES ?? 0) >= 0;
      const asignado = esTesoreroAsignado(r);
      return !confirmado || !noVencida || !asignado;
    });

    if (excluidas.length > 0) {
      const foliosExcluidos = excluidas.map(r => r.FOLIO).join(', ');
      if (validas.length === 0) {
        // Todas están excluidas — no se puede continuar
        handleOpenSweetAlert(
          `No hay OC válidas para solicitar fondos.\n\nLas siguientes OC están vencidas, sin precio confirmado o no estás asignado:\n${foliosExcluidos}`,
          'warning'
        );
        return;
      }
      // Hay algunas válidas — preceder con advertencia usando SweetAlert
      import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
          title: 'Algunas OC no serán incluidas',
          html: `<p>Las siguientes OC están <b>vencidas, sin confirmar, o no estás asignado</b> y serán excluidas:</p>
                 <p style="color:#e53935; font-weight:600;">${foliosExcluidos}</p>
                 <p>Solo se solicitarán las <b>${validas.length} OC válidas</b>.</p>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: `Continuar con ${validas.length} OC(s)`,
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#0655A5',
        }).then((result) => {
          if (result.isConfirmed) {
            setSelectedRows(validas);
            setModalFondosOpen(true);
          }
        });
      });
      return;
    }

    // Todas son válidas
    setModalFondosOpen(true);
  }, [selectedRows, setSelectedRows, esTesoreroAsignado]);

  const openPagoMasivoModal = useCallback(() => {
    if (!selectedRows.length) return;

    // Split into valid (confirmed+assigned) and excluded (unconfirmed or not assigned)
    const validas = selectedRows.filter(r => Number(r.CONFIRMARPRECIO) === 1 && esTesoreroAsignado(r));
    const excluidas = selectedRows.filter(r => Number(r.CONFIRMARPRECIO) !== 1 || !esTesoreroAsignado(r));

    if (excluidas.length > 0) {
      const foliosExcluidos = excluidas.map(r => r.FOLIO).join(', ');
      if (validas.length === 0) {
        handleOpenSweetAlert(
          `No hay OC válidas para procesar.\n\nLas siguientes OC no tienen el precio confirmado o corresponden a otro tesorero:\n${foliosExcluidos}`,
          'warning'
        );
        return;
      }
      import('sweetalert2').then(({ default: Swal }) => {
        Swal.fire({
          title: 'Algunas OC no serán incluidas',
          html: `<p>Las siguientes OC tienen el <b>precio sin confirmar o no estás asignado</b> y serán excluidas:</p>
                 <p style="color:#e53935; font-weight:600;">${foliosExcluidos}</p>
                 <p>Solo se procesarán las <b>${validas.length} OC confirmadas y asignadas a ti</b>.</p>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: `Continuar con ${validas.length} OC(s)`,
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#09814A',
        }).then((result) => {
          if (result.isConfirmed) {
            setSelectedRows(validas);
            setModalPagoMasivoOpen(true);
          }
        });
      });
      return;
    }

    setModalPagoMasivoOpen(true);
  }, [selectedRows, setSelectedRows, esTesoreroAsignado]);

  return {
    data, loading, loadingModal, loadingModalMessage, setLoadingModal, setLoadingModalMessage,
    fileModalOpen, setFileModalOpen, fileList, initialFileId,
    modalStates, setModalStates, snackbar, setSnackbar,

    empresaIds, empresaIdPref, usuarioId, loggedInUsername, esTesorero, hiddenFieldsMemo,
    selectedRows, setSelectedRows, modalFondosOpen, setModalFondosOpen,
    modalPagoMasivoOpen, setModalPagoMasivoOpen,
    tempFilterData, setTempFilterData, ejecutarBusquedaManual, tableParams,
    esTesoreroAsignado, fetchData, handleTableStateChange, handleLoadMore, handleExportFetchAll,
    correoConfirmarPrecio, handleViewFile, handleViewSpecificFile, openSolicitarFondosModal, openPagoMasivoModal, apiUrl
  };
};
